import {ChangeDetectorRef, Component, ElementRef, OnInit, QueryList, signal, ViewChild, ViewChildren} from '@angular/core';
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray} from '@angular/cdk/drag-drop';
import {CommonModule} from '@angular/common';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {AdminFormService} from '../../control/admin-form.service';
import {ChecklistItem, FormField} from '../../entity/admin-form.model';
import {LocalizationPipePipe} from '../../../localization/control/localisation-pipe-pipe';
import {LocalizationService} from '../../../localization/control/localization.service';
import {FileUploadConfig, FileUploads} from '../../../forms/boundary/file-uploads/file-uploads';
import {environment} from '../../../environments/environment';
import {ConfirmationDialog} from '../confirmation-dialog/confirmation-dialog';

@Component({
  selector: 'app-form-editor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, LocalizationPipePipe, FileUploads, ConfirmationDialog, CdkDropList, CdkDrag, CdkDragHandle],
  templateUrl: './form-editor.html',
  styleUrl: './form-editor.scss'
})
export class FormEditor implements OnInit {
  form: FormGroup;
  fields: FormField[] = [];
  customMetadataFields: FormArray;
  submitting = false;
  loading = false;
  error?: string;

  isEditMode = false;
  editFormId?: string;
  editVersion?: string;

  existingLogoUrl?: string;
  logoRemoved = false;

  // Landing page state
  landingPageEnabled = false;
  checklistItems: ChecklistItem[] = [];
  landingPageTitleError = false;
  landingPageChecklistError = false;

  // Transition action state
  formStatus = signal<string | undefined>(undefined);
  publishLoading = signal(false);
  dryRunLoading = signal(false);
  unpublishLoading = signal(false);
  deleteLoading = signal(false);
  showPublishDialog = signal(false);
  showDeleteDialog = signal(false);
  showUnpublishDialog = signal(false);
  actionSuccess = signal<string | null>(null);
  actionError = signal<string | null>(null);

  logoUploadConfig: FileUploadConfig = {
    title: 'Logo',
    subtitle: 'Upload a logo for this form',
    icon: 'image',
    multiple: false,
    acceptedTypes: ['PNG', 'JPG', 'SVG'],
    maxSize: '2MB',
    dropzoneText: 'fileUpload.dropzoneText.logo',
    optional: true,
    actionName: 'Upload'
  };

  @ViewChild('logoUpload') logoUploadComponent?: FileUploads;
  @ViewChildren('moveUpBtn') moveUpButtons!: QueryList<ElementRef>;
  @ViewChildren('moveDownBtn') moveDownButtons!: QueryList<ElementRef>;
  @ViewChildren('checklistMoveUpBtn') checklistMoveUpButtons!: QueryList<ElementRef>;
  @ViewChildren('checklistMoveDownBtn') checklistMoveDownButtons!: QueryList<ElementRef>;

  private readonly MAX_LOGO_SIZE_BYTES = 2_097_152; // 2 MB

  fieldTypes: {value: FormField['type']; labelKey: string}[] = [
    {value: 'text', labelKey: 'admin.editor.field.type.text'},
    {value: 'email', labelKey: 'admin.editor.field.type.email'},
    {value: 'number', labelKey: 'admin.editor.field.type.number'},
    {value: 'textarea', labelKey: 'admin.editor.field.type.textarea'},
    {value: 'select', labelKey: 'admin.editor.field.type.select'},
    {value: 'checkbox', labelKey: 'admin.editor.field.type.checkbox'},
    {value: 'files', labelKey: 'admin.editor.field.type.file'},
    {value: 'radio', labelKey: 'admin.editor.field.type.radio'},
    {value: 'autocomplete', labelKey: 'admin.editor.field.type.autocomplete'}
  ];

  // Autocomplete data source upload state
  uploadingSourceId: string | null = null;
  uploadError: string | null = null;
  uploadSuccess: Record<string, number> = {};

  constructor(
    private readonly fb: FormBuilder,
    private readonly adminFormService: AdminFormService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    private readonly localizationService: LocalizationService
  ) {
    this.form = this.fb.group({
      formId: ['', Validators.required],
      version: ['v1.0.0', Validators.required],
      title: ['', Validators.required],
      // Metadata fields
      insuranceCompany: ['', Validators.required],
      natCatType: ['', Validators.required],
      natCatName: ['', Validators.required],
      location: ['', Validators.required],
      year: ['', Validators.required],
      egressEmail: ['', [Validators.required, Validators.email]]
    });

    this.customMetadataFields = this.fb.array([]);
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      if (params['formId'] && params['version']) {
        this.isEditMode = true;
        this.editFormId = params['formId'];
        this.editVersion = params['version'];
        this.loadForm();
      }
    });
  }

  private async loadForm(): Promise<void> {
    if (!this.editFormId || !this.editVersion) return;

    this.loading = true;
    this.cdr.detectChanges();

    try {
      const formDetail = await this.adminFormService.getForm(this.editFormId, this.editVersion);

      this.formStatus.set(formDetail.status);

      this.form.patchValue({
        formId: formDetail.formId,
        version: formDetail.version,
        title: formDetail.title
      });

      this.form.get('formId')?.disable();
      this.form.get('version')?.disable();

      this.fields = formDetail.definition?.fields || [];

      // Load logo preview if form has a logo
      if (formDetail.logoS3Key) {
        this.existingLogoUrl = `${environment.fnolPublishedFormsBackend.baseUrl}/${formDetail.logoS3Key}`;
      }

      // Load metadata if present
      if (formDetail.definition?.metadata) {
        const metadata = formDetail.definition.metadata;
        this.form.patchValue({
          insuranceCompany: metadata.insuranceCompany,
          natCatType: metadata.natCatType,
          natCatName: metadata.natCatName,
          location: metadata.location,
          year: metadata.year,
          egressEmail: metadata.egressEmail
        });

        // Load custom fields
        if (metadata.customFields) {
          Object.entries(metadata.customFields).forEach(([key, value]) => {
            this.addCustomMetadataField(key, value);
          });
        }
      }

      // Load landing page if present
      if (formDetail.definition?.landingPage && formDetail.definition.landingPage.checklistItems?.length > 0) {
        this.toggleLandingPage(true);
        this.form.patchValue({
          landingPageTitle: formDetail.definition.landingPage.title || '',
          landingPageDescription: formDetail.definition.landingPage.description || ''
        });
        this.checklistItems = formDetail.definition.landingPage.checklistItems.map((item: ChecklistItem) => ({ label: item.label }));
      } else {
        this.landingPageEnabled = false;
      }
    } catch (err: unknown) {
      this.error = err instanceof Error ? err.message : this.localizationService.getTranslation('admin.editor.error.load');
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  addField(): void {
    this.fields.push({
      id: '',
      type: 'text',
      label: '',
      required: false
    });
  }

  toggleLandingPage(enabled: boolean): void {
    this.landingPageEnabled = enabled;
    if (enabled) {
      this.form.addControl('landingPageTitle', this.fb.control('', Validators.required));
      this.form.addControl('landingPageDescription', this.fb.control(''));
    } else {
      this.form.removeControl('landingPageTitle');
      this.form.removeControl('landingPageDescription');
      this.landingPageTitleError = false;
      this.landingPageChecklistError = false;
    }
  }

  addChecklistItem(): void {
    this.checklistItems.push({ label: '' });
  }

  removeChecklistItem(index: number): void {
    this.checklistItems.splice(index, 1);
  }

  updateChecklistItemLabel(index: number, label: string): void {
    this.checklistItems[index] = { label };
  }

  moveChecklistItemUp(index: number): void {
    if (index > 0) {
      const temp = this.checklistItems[index];
      this.checklistItems[index] = this.checklistItems[index - 1];
      this.checklistItems[index - 1] = temp;
      setTimeout(() => {
        this.checklistMoveUpButtons.get(index - 1)?.nativeElement.focus();
      });
    }
  }

  moveChecklistItemDown(index: number): void {
    if (index < this.checklistItems.length - 1) {
      const temp = this.checklistItems[index];
      this.checklistItems[index] = this.checklistItems[index + 1];
      this.checklistItems[index + 1] = temp;
      setTimeout(() => {
        this.checklistMoveDownButtons.get(index + 1)?.nativeElement.focus();
      });
    }
  }

  onChecklistItemDrop(event: CdkDragDrop<ChecklistItem[]>): void {
    moveItemInArray(this.checklistItems, event.previousIndex, event.currentIndex);
  }

  isFirstChecklistItem(index: number): boolean {
    return index === 0;
  }

  isLastChecklistItem(index: number): boolean {
    return index === this.checklistItems.length - 1;
  }

  removeLogo(): void {
    this.existingLogoUrl = undefined;
    this.logoRemoved = true;
  }

  removeField(index: number): void {
    this.fields.splice(index, 1);
  }

  updateField(index: number, property: keyof FormField, value: unknown): void {
    const field = this.fields[index];
    if (property === 'id') field.id = value as string;
    else if (property === 'label') field.label = value as string;
    else if (property === 'type') field.type = value as FormField['type'];
    else if (property === 'required') field.required = value as boolean;
    else if (property === 'sourceId') field.sourceId = value as string;
  }

  getExistingSourceIds(): string[] {
    return [...new Set(
      this.fields
        .filter(f => f.type === 'autocomplete' && f.sourceId)
        .map(f => f.sourceId!)
    )];
  }

  async uploadDataSource(fieldIndex: number, event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const field = this.fields[fieldIndex];
    const formValue = this.form.getRawValue();
    const sourceId = field.sourceId || field.id;

    // Assign sourceId if not set
    if (!field.sourceId) {
      field.sourceId = sourceId;
    }

    this.uploadingSourceId = sourceId;
    this.uploadError = null;

    try {
      // Validate JSON client-side first
      const text = await file.text();
      try {
        JSON.parse(text);
      } catch {
        this.uploadError = this.localizationService.getTranslation('admin.editor.autocomplete.error.invalidJson');
        this.uploadingSourceId = null;
        this.cdr.detectChanges();
        return;
      }

      // Get presigned URL
      const response = await this.adminFormService.getDataSourceUploadUrl(
        formValue.formId, formValue.version, sourceId
      );

      // Upload to S3
      await this.adminFormService.uploadDataSourceToS3(response.presignedUrl, file);

      // Process the uploaded file
      const processResult = await this.adminFormService.processDataSource(
        formValue.formId, formValue.version, sourceId
      );

      this.uploadSuccess[sourceId] = processResult.entryCount;
      this.uploadError = null;
    } catch (err: unknown) {
      this.uploadError = err instanceof Error ? err.message : this.localizationService.getTranslation('admin.editor.autocomplete.error.uploadFailed');
    } finally {
      this.uploadingSourceId = null;
      this.cdr.detectChanges();
    }
  }

  assignExistingSourceId(fieldIndex: number, sourceId: string): void {
    this.fields[fieldIndex].sourceId = sourceId;
  }

  moveFieldUp(index: number): void {
    if (index > 0) {
      const temp = this.fields[index];
      this.fields[index] = this.fields[index - 1];
      this.fields[index - 1] = temp;
      setTimeout(() => {
        this.moveUpButtons.get(index - 1)?.nativeElement.focus();
      });
    }
  }

  moveFieldDown(index: number): void {
    if (index < this.fields.length - 1) {
      const temp = this.fields[index];
      this.fields[index] = this.fields[index + 1];
      this.fields[index + 1] = temp;
      setTimeout(() => {
        this.moveDownButtons.get(index + 1)?.nativeElement.focus();
      });
    }
  }

  onFieldDrop(event: CdkDragDrop<FormField[]>): void {
    moveItemInArray(this.fields, event.previousIndex, event.currentIndex);
  }

  isFirstField(index: number): boolean {
    return index === 0;
  }

  isLastField(index: number): boolean {
    return index === this.fields.length - 1;
  }


  addCustomMetadataField(key: string = '', value: string = ''): void {
    if (this.customMetadataFields.length >= 5) {
      this.error = this.localizationService.getTranslation('admin.editor.metadata.error.maxCustomFields');
      return;
    }

    const fieldGroup = this.fb.group({
      key: [key, [Validators.required, this.alphanumericValidator(), this.noPredefinedKeyValidator()]],
      value: [value, Validators.required]
    });

    this.customMetadataFields.push(fieldGroup);
  }

  removeCustomMetadataField(index: number): void {
    this.customMetadataFields.removeAt(index);
  }

  private alphanumericValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const isValid = /^[a-zA-Z0-9]+$/.test(value);
      return isValid ? null : { alphanumeric: true };
    };
  }

  private noPredefinedKeyValidator(): ValidatorFn {
    const predefinedKeys = ['insuranceCompany', 'natCatType', 'natCatName', 'location', 'year', 'egressEmail'];

    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const conflicts = predefinedKeys.includes(value);
      return conflicts ? { predefinedKeyConflict: true } : null;
    };
  }

  async onSubmit(): Promise<void> {
      // Clear landing page inline errors
      this.landingPageTitleError = false;
      this.landingPageChecklistError = false;

      if (this.form.invalid || this.customMetadataFields.invalid) {
        // Check if the form is invalid due to landing page title
        if (this.landingPageEnabled) {
          const titleValue = this.form.get('landingPageTitle')?.value?.trim();
          if (!titleValue) {
            this.landingPageTitleError = true;
          }
        }
        this.error = this.localizationService.getTranslation('admin.editor.error.validation');
        return;
      }

      const invalidFields = this.fields.some(f => !f.id || !f.label);
      if (this.fields.length === 0 || invalidFields) {
        this.error = this.localizationService.getTranslation('admin.editor.error.validation');
        return;
      }

      // Validate landing page checklist when toggle is enabled
      if (this.landingPageEnabled) {
        const hasValidChecklistItem = this.checklistItems.some(item => item.label.trim() !== '');
        if (!hasValidChecklistItem) {
          this.landingPageChecklistError = true;
          this.error = this.localizationService.getTranslation('admin.editor.error.validation');
          return;
        }
      }

      // Validate logo file size before proceeding
      const stagedLogoFile = this.logoUploadComponent?.selectedFiles?.[0];
      if (stagedLogoFile && stagedLogoFile.size > this.MAX_LOGO_SIZE_BYTES) {
        this.error = 'Logo file exceeds the maximum allowed size of 2 MB.';
        return;
      }

      this.submitting = true;
      this.error = undefined;

      try {
        const formValue = this.form.getRawValue();

        // Handle logo upload or deletion before saving the form
        await this.handleLogoBeforeSave(formValue.formId, formValue.version);

        // Build metadata object
        const metadata: {
          insuranceCompany: string;
          natCatType: string;
          natCatName: string;
          location: string;
          year: string;
          egressEmail: string;
          customFields?: Record<string, string>;
        } = {
          insuranceCompany: formValue.insuranceCompany,
          natCatType: formValue.natCatType,
          natCatName: formValue.natCatName,
          location: formValue.location,
          year: formValue.year,
          egressEmail: formValue.egressEmail
        };

        // Add custom fields if present
        if (this.customMetadataFields.length > 0) {
          metadata.customFields = {};
          this.customMetadataFields.controls.forEach((control) => {
            const group = control as FormGroup;
            const key = group.get('key')?.value;
            const value = group.get('value')?.value;
            if (key && value) {
              metadata.customFields![key] = value;
            }
          });
        }

        await this.adminFormService.saveForm({
          formId: formValue.formId,
          version: formValue.version,
          status: 'draft',
          title: formValue.title,
          definition: {
            fields: this.fields,
            metadata: metadata,
            ...(this.landingPageEnabled ? {
              landingPage: {
                title: this.form.get('landingPageTitle')?.value?.trim(),
                description: this.form.get('landingPageDescription')?.value?.trim() || undefined,
                checklistItems: this.checklistItems.filter(item => item.label.trim() !== '')
              }
            } : {})
          }
        });
        await this.router.navigate(['/admin/forms']);
      } catch (err: unknown) {
        this.error = err instanceof Error ? err.message : this.localizationService.getTranslation(this.isEditMode ? 'admin.editor.error.save' : 'admin.editor.error.create');
      } finally {
        this.submitting = false;
      }
    }

  private async handleLogoBeforeSave(formId: string, version: string): Promise<void> {
    const stagedLogoFile = this.logoUploadComponent?.selectedFiles?.[0];

    if (stagedLogoFile) {
      // New logo staged — upload it via presigned URL
      const extension = this.getFileExtension(stagedLogoFile.name);
      const { presignedUrl } = await this.adminFormService.getLogoUploadUrl(formId, version, extension);
      await this.adminFormService.uploadLogoToS3(presignedUrl, stagedLogoFile);
    } else if (this.logoRemoved) {
      // Logo was removed and no new one staged — delete from S3
      await this.adminFormService.deleteLogo(formId, version);
    }
  }

  private getFileExtension(fileName: string): string {
    const parts = fileName.split('.');
    return parts[parts.length - 1]?.toLowerCase() ?? '';
  }

  // --- Transition action methods ---

  openPublishDialog(): void {
    this.showPublishDialog.set(true);
  }

  closePublishDialog(): void {
    this.showPublishDialog.set(false);
  }

  async confirmPublish(): Promise<void> {
    if (!this.editFormId || !this.editVersion) return;

    this.publishLoading.set(true);
    this.actionError.set(null);

    try {
      await this.adminFormService.publishForm(this.editFormId, this.editVersion);
      this.closePublishDialog();
      await this.router.navigate(['/admin/forms']);
    } catch (err: unknown) {
      this.actionError.set(
        err instanceof Error ? err.message : this.localizationService.getTranslation('admin.forms.error.publish')
      );
    } finally {
      this.publishLoading.set(false);
      this.cdr.detectChanges();
    }
  }

  openDeleteDialog(): void {
    this.showDeleteDialog.set(true);
  }

  closeDeleteDialog(): void {
    this.showDeleteDialog.set(false);
  }

  async confirmDelete(): Promise<void> {
    if (!this.editFormId || !this.editVersion) return;

    this.deleteLoading.set(true);
    this.actionError.set(null);

    try {
      await this.adminFormService.deleteForm(this.editFormId, this.editVersion);
      this.closeDeleteDialog();
      await this.router.navigate(['/admin/forms']);
    } catch (err: unknown) {
      this.actionError.set(
        err instanceof Error ? err.message : this.localizationService.getTranslation('admin.forms.error.delete')
      );
    } finally {
      this.deleteLoading.set(false);
      this.cdr.detectChanges();
    }
  }

  openUnpublishDialog(): void {
    this.showUnpublishDialog.set(true);
  }

  closeUnpublishDialog(): void {
    this.showUnpublishDialog.set(false);
  }

  async confirmUnpublish(): Promise<void> {
    if (!this.editFormId || !this.editVersion) return;

    this.unpublishLoading.set(true);
    this.actionError.set(null);

    try {
      await this.adminFormService.unpublishForm(this.editFormId, this.editVersion);
      this.closeUnpublishDialog();
      await this.loadForm();
      this.actionSuccess.set(this.localizationService.getTranslation('admin.forms.unpublish.success'));
      this.cdr.detectChanges();

      setTimeout(() => {
        this.actionSuccess.set(null);
        this.cdr.detectChanges();
      }, 3000);
    } catch (err: unknown) {
      this.actionError.set(
        err instanceof Error ? err.message : this.localizationService.getTranslation('admin.forms.error.unpublish')
      );
    } finally {
      this.unpublishLoading.set(false);
      this.cdr.detectChanges();
    }
  }

  async dryRun(): Promise<void> {
    if (!this.editFormId || !this.editVersion) return;

    this.dryRunLoading.set(true);
    this.actionError.set(null);
    this.actionSuccess.set(null);

    try {
      await this.adminFormService.dryRunForm(this.editFormId, this.editVersion);
      await this.loadForm();
      this.actionSuccess.set(this.localizationService.getTranslation('admin.forms.dryrun.success'));
      this.cdr.detectChanges();

      setTimeout(() => {
        this.actionSuccess.set(null);
        this.cdr.detectChanges();
      }, 3000);
    } catch (err: unknown) {
      this.actionError.set(
        err instanceof Error ? err.message : this.localizationService.getTranslation('admin.forms.error.dryrun')
      );
    } finally {
      this.dryRunLoading.set(false);
      this.cdr.detectChanges();
    }
  }

  dismissActionSuccess(): void {
    this.actionSuccess.set(null);
  }

  dismissActionError(): void {
    this.actionError.set(null);
  }

}
